import { Injectable } from '@angular/core';
import { HeaderContent } from '../models/headerContent';
import type { USGenericComponent } from '../upsells-downsells/upsells/us-generic/us-generic.component';

/** One block entry from a page's JSON `pageContents` array. */
export interface PageBlock {
  type: string;
  name?: string;
  componentName?: string;
  class?: string;
  options?: Record<string, any>;
  html?: string;
}

/** What a handler returns: the assembled `options` for a block, plus optional render overrides. */
export interface BlockResult {
  options: unknown;
  componentName?: string;
  class?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PageContentBuilderService {

  constructor() { }

  private buildHeaderOptions(context: USGenericComponent): HeaderContent {
    return { currency: context?.currency || 'USD' };
  }

  private buildSectionContent(raw: any): Array<any> {
    if (raw == null) return [];
    return Array.isArray(raw) ? raw : [{ blocks: [`${raw}`] }];
  }

  // `context` is the USGenericComponent instance. Kept loosely typed on the public signature so
  // tests can pass partial mocks; the handler map below types `ctx` precisely (cast once below).
  buildPageContents(contents: PageBlock[], context: any): any[] {
    const handlers: Record<string, (ctx: USGenericComponent, content?: PageBlock) => BlockResult> = {
      'brand-header': (ctx, content) => ({ options: { ...this.buildHeaderOptions(ctx), ...(content?.options || {}) }, class: content?.class || 'section-brand-header' }),
      'upsell-banner': () => ({ options: {} }),
      'upsell-buttons': () => ({ options: {} }),
      'upsell-mbg': () => ({ options: {} }),
      'upsell-wait': () => ({ options: {} }),
      'upsell-section-1': ctx => ({ options: this.buildSectionContent(ctx.section01Data), componentName: 'upsell-section' }),
      'upsell-section-2': ctx => ({ options: this.buildSectionContent(ctx.section02Data), componentName: 'upsell-section' }),
      'upsell-section-3': ctx => ({ options: this.buildSectionContent(ctx.section03Data), componentName: 'upsell-section' }),
      'upsell-product-box': () => ({ options: {} }),
      'upsell-multi-selector': () => ({ options: {} }),
      'upsell-guarantee': () => ({ options: {} }),
      'upsell-why': ctx => ({ options: ctx.upsellWhyContent }),
      'upsell-faq': ctx => ({ options: ctx.upsellFaqContent }),
      'upsell-sticky-bar': (ctx, content) => ({ options: content?.options || {} }),
      'vidalytics-video': () => ({ options: {}, componentName: 'vidalytics-video' }),
      'upsell-modal': (ctx, content) => ({ options: content?.options || {} }),
      'brand-footer': () => ({ options: {} }),
      'upsell-slider': (ctx, content) => ({ options: content?.options || {} }),
    };

    return contents.map((content, index) => {
      if (content.type === 'component') {
        const handler = content.componentName ? handlers[content.componentName] : undefined;
        if (handler) {
          const result = handler(context as USGenericComponent, content);
          return {
            name: `content${index}`,
            type: 'component',
            componentName: result.componentName || content.componentName,
            options: result.options,
            class: result.class || content.class,
          };
        }
      } else if (content.type === 'html') {
        return { name: content.name, type: 'html', html: content.html, class: content.class };
      }
      return null;
    }).filter(Boolean);
  }
}
